/**
 * Download a server checkpoint entry and install it as the editable socket.
 *
 * Extracted out of `useBkCheckpointMeshLoad` so it can be used from the undo
 * resolver without an import cycle: that hook imports the `step-checkpoints`
 * feature index, and the index exports the undo modules, so an undo module
 * importing the hook would close the loop — and the hook registers the replay
 * engine's server anchor resolver at module scope, which is exactly the kind of
 * side effect a half-initialised cycle turns into a TypeError.
 *
 * This module depends only on the editor store and the file-meta helpers.
 */
import useEditorStore from '../stores/useEditorStore';
import {
  detectMeshFileType,
  getCheckpointMeshUrl,
  loadCheckpointMeshFromUrl,
} from './bkScanSourceFiles';

/** How long to wait for ThreeScene to swap the newly-set model into the scene. */
const SWAP_TIMEOUT_MS = 10000;
const SWAP_POLL_MS = 32;

/**
 * Resolve once ThreeScene has swapped `shadedMesh` to a different mesh than
 * `prevUuid` (i.e. the newly-set model is actually in the scene). Times out so a
 * failed load can't hang the caller forever.
 */
export function waitForShadedMeshReplaced(prevUuid, signal, timeoutMs = SWAP_TIMEOUT_MS) {
  return new Promise((resolve) => {
    const start = performance.now();
    const tick = () => {
      if (signal?.aborted) return resolve(false);
      const m = useEditorStore.getState().sceneRefs?.shadedMesh;
      const ready =
        (m?.geometry?.attributes?.position?.count || 0) > 0 && m.uuid !== prevUuid;
      if (ready) return resolve(true);
      if (performance.now() - start > timeoutMs) return resolve(false);
      setTimeout(tick, SWAP_POLL_MS);
    };
    tick();
  });
}

/**
 * Download a checkpoint entry and install it as the editor socket, then wait
 * until ThreeScene has actually swapped it into the scene.
 *
 * @param {object|null|undefined} entry - a `get_bk_design_files_meta` checkpoint entry
 * @param {{ preprocessed?: boolean, signal?: AbortSignal }} [opts]
 * @returns {Promise<boolean>} false when the entry has no usable mesh
 */
export async function installCheckpointSocket(entry, { preprocessed = false, signal } = {}) {
  const url = getCheckpointMeshUrl(entry);
  if (!url) return false;

  const type =
    entry?.mesh_format === 'ply' || entry?.mesh_format === 'stl'
      ? entry.mesh_format
      : detectMeshFileType(entry?.file_name || '') || 'ply';
  if (type !== 'stl' && type !== 'ply') return false;

  const loaded = await loadCheckpointMeshFromUrl(url, type, signal);
  const prevUuid = useEditorStore.getState().sceneRefs?.shadedMesh?.uuid ?? null;
  useEditorStore.getState().setUploadedModelData({
    ...loaded,
    source: 'checkpoint',
    preferredScaleUnit: 'mm',
    ...(preprocessed ? { preprocessed: true } : {}),
  });
  await waitForShadedMeshReplaced(prevUuid, signal);
  return true;
}
